// Phase 35d — Personalized Copilot suggestion chips.
//
// Fetches `/api/copilot/tools/personalized` for chip prompts tailored to
// the user's actual portfolio (specific scores, addresses, deal types).
// Falls back to the original Phase 35c static set if the endpoint errors
// or returns "fallback_too_few_deals".
//
// Behaviour preserved from Phase 35c:
//   • Empty state: chips render as a row above the input
//   • After 1+ message: chips collapse to a tiny "💡 Suggestions" button
//     that re-expands them inline
//   • Clicking a chip ONLY prefills the input — never auto-sends
//   • Collapses cleanly on the empty→after-messages transition
//     (fixed in commit ec3c4ad)
//
// TestID convention: `copilot-chip-{slug}` (e.g. `copilot-chip-compare-my-top-vs-bottom-rental`).

import { useEffect, useState } from "react";
import { Lightbulb, ChevronDown, ChevronUp, Sparkles } from "lucide-react";
import api from "@/lib/api";

// Static fallbacks (same as Phase 35c — kept for when personalisation
// fails or the user has <2 deals).
const STATIC_DEAL_FOCUS = [
  { slug: "run-sensitivity",     label: "Run sensitivity on rent vs vacancy",
    prompt: "Run a sensitivity analysis on monthly rent (±15%) versus vacancy (±5%) for this deal." },
  { slug: "find-similar",        label: "Find similar deals in my portfolio",
    prompt: "Find the most similar deals in my portfolio to this one and explain what makes them comparable." },
  { slug: "top-risks",           label: "What are the top risks here?",
    prompt: "What are the top 3 risks of this deal and how would you mitigate each?" },
  { slug: "rent-drop-20",        label: "What if rent drops 20%?",
    prompt: "What would my cash flow, DSCR and verdict look like if rent dropped 20%? Use calculate_metrics." },
];

const STATIC_PORTFOLIO = [
  { slug: "compare-top",         label: "Compare my top 3 performing deals",
    prompt: "Compare my top 3 highest-scoring deals side-by-side and tell me what's driving the score gap." },
  { slug: "best-risk-adjusted",  label: "Which deal has the best risk-adjusted return?",
    prompt: "Which deal in my portfolio has the best risk-adjusted return? Consider cap rate, DSCR, and deal score together." },
  { slug: "find-underperformers", label: "Find underperformers in my portfolio",
    prompt: "Query my portfolio for deals with deal_score below 50 — these are the underperformers." },
  { slug: "rentals-only",        label: "Show me only my rental deals",
    prompt: "Query my portfolio and list only my rental-type deals, sorted by cap rate descending." },
];

export default function SuggestionChips({
  hasDealFocus,
  hasMessages,
  onPick,
  dealId,
}) {
  const [expanded, setExpanded] = useState(!hasMessages);
  const [chips, setChips] = useState(hasDealFocus ? STATIC_DEAL_FOCUS : STATIC_PORTFOLIO);
  const [source, setSource] = useState("fallback_static_initial");
  const [loading, setLoading] = useState(false);

  // Sync collapsed state when the empty→after-messages transition fires.
  useEffect(() => {
    if (hasMessages) setExpanded(false);
  }, [hasMessages]);

  // Fetch personalized chips when (dealId or focus) changes. Race-safe.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const url = dealId
          ? `/copilot/tools/personalized?deal_id=${encodeURIComponent(dealId)}`
          : "/copilot/tools/personalized";
        const { data } = await api.get(url);
        if (cancelled) return;
        if (Array.isArray(data.chips) && data.chips.length > 0) {
          setChips(data.chips);
          setSource(data.source || "unknown");
        }
      } catch {
        // Keep the static fallback already loaded — nothing more to do.
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealId, hasDealFocus]);

  const isPersonalized = source === "llm_fresh" || source === "cache_hit"
    || source === "cache_hit_sig_unchanged";

  // Empty state: full row.
  if (!hasMessages) {
    return (
      <div data-testid="copilot-chips-row" className="mb-3" data-source={source}>
        <p className="mb-2 flex items-center gap-1.5 text-xs font-medium text-slate-600">
          {isPersonalized ? (
            <Sparkles className="h-3.5 w-3.5 text-indigo-500" />
          ) : (
            <Lightbulb className="h-3.5 w-3.5 text-amber-500" />
          )}
          {isPersonalized ? "Tailored to your portfolio:" : "Try one of these:"}
          {loading && (
            <span className="ml-1 text-[10px] text-slate-400">refreshing…</span>
          )}
        </p>
        <div className="flex flex-wrap gap-2">
          {chips.map((c) => (
            <button
              key={c.slug}
              type="button"
              onClick={() => onPick(c.prompt)}
              data-testid={`copilot-chip-${c.slug}`}
              className={`group rounded-full border px-3 py-1.5 text-xs font-medium transition-all hover:-translate-y-0.5 hover:shadow-sm ${
                isPersonalized
                  ? "border-indigo-300 bg-gradient-to-r from-indigo-50 to-violet-50 text-indigo-700 hover:border-indigo-400 hover:from-indigo-100 hover:to-violet-100"
                  : "border-indigo-200 bg-indigo-50/60 text-indigo-700 hover:border-indigo-300 hover:bg-indigo-100"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>
      </div>
    );
  }

  // After messages: collapsible.
  return (
    <div className="mb-3" data-testid="copilot-chips-collapsed" data-source={source}>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        data-testid="copilot-chips-toggle"
        className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs text-slate-600 transition-all hover:border-indigo-200 hover:bg-indigo-50 hover:text-indigo-700"
      >
        {isPersonalized ? (
          <Sparkles className="h-3.5 w-3.5 text-indigo-500" />
        ) : (
          <Lightbulb className="h-3.5 w-3.5 text-amber-500" />
        )}
        Suggestions
        {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
      </button>
      {expanded && (
        <div className="mt-2 flex flex-wrap gap-2">
          {chips.map((c) => (
            <button
              key={c.slug}
              type="button"
              onClick={() => onPick(c.prompt)}
              data-testid={`copilot-chip-${c.slug}`}
              className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs text-slate-700 transition-all hover:-translate-y-0.5 hover:border-indigo-200 hover:bg-indigo-50 hover:text-indigo-700"
            >
              {c.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
